// assistantRecords.js — THE RECORDS BLOCK v1, client half (AI_ASSISTANT_SPEC v1
// §2 + §3; DESIGN-WL-ASSISTANT-MAIN-VIEW-1, lane current-23-assistant; the
// server half is lane current-23-aib, src/domains/ai-boards recordsBlock.js).
//
// A records block is DECLARATIVE: the model names a collection and how to read
// it, never a number. ONE shape, shared by the Dashboard board items (kind
// 'records') and the thread's inline cards ({ type: 'records', block }):
//   { kind: 'records', app, collection, view: 'stat'|'list'|'table'|'chart',
//     title: { key?, text? }, filters: [{ field, op, value }],
//     period?: { field, preset }, aggregate?: { op, field? }, groupBy?,
//     sort?: { field, dir }, limit?: 1..50, fields?: [field] }
// The rows come from ONE SDK call, `sdk.execute('ai.boards', 'records',
// { workspaceId, block })`, which the server resolves under the member's
// grants → { rows: [{ id, title, values, refs? }], total, aggregate, groups,
// fields, period }. The client caches each answer per workspace + block
// (`root._aiRecords`, workspace-scoped) so the same block in the thread and on
// a board reads once, and a thread moved to the sidebar re-renders without a
// refetch. Until the SDK ships the op, the answer is 'unavailable' — the dev
// fixture answers ONLY behind localStorage `symbols_ai_records_fixture = '1'`
// (no fixture data in any product path).
//
// Frank note: el.call serializes each registered export on its own (FA204),
// so every export here is self-contained — vocabularies are restated.

// The block a surface carries: a board row (`records`), a thread card
// (`{ type: 'records', block }`) or the block itself — normalized, or null.
export const recordsBlockOf = function recordsBlockOf(s) {
  const x = s && typeof s === 'object' ? s : null
  if (!x) return null
  const raw =
    x.records && typeof x.records === 'object'
      ? x.records
      : x.type === 'records'
        ? x.block
        : x.kind === 'records'
          ? x
          : null
  return this.call('recordsBlockNormalize', raw)
}

// A records block's CONTENT size — the one rule the generated board and the
// home share (WIDGET-TOOLS-RECORDS-SHAPE-1): one number is S; a chart is M; a
// table wider than three columns takes the full row (XL); a list or table of
// up to six rows is one row of two cells (M), more rows take the second row
// (L). Pure: the block in, a size out.
export const recordsContentSize = function recordsContentSize(block) {
  const b = block && typeof block === 'object' ? block : {}
  const rowsWanted = Number(b.limit) || 10
  const cols = Array.isArray(b.fields) ? b.fields.length : 0
  if (b.view === 'stat') return 's'
  if (b.view === 'chart') return 'm'
  if (b.view === 'table' && cols > 3) return 'xl'
  return rowsWanted <= 6 ? 'm' : 'l'
}

// RECORDS BLOCK v1 → its normalized form, or null. Lenient on shape, strict on
// vocabulary: unknown ops / presets / views drop, never throw. The server is
// the authority (it validates the block against the collection's fields).
export const recordsBlockNormalize = function recordsBlockNormalize(raw) {
  const VIEWS = ['stat', 'list', 'table', 'chart']
  const OPS = ['eq', 'ne', 'in', 'gte', 'lte', 'between', 'contains']
  const PRESETS = ['today', 'thisWeek', 'thisMonth', 'thisQuarter', 'lastQuarter', 'thisYear']
  const AGG = ['count', 'sum', 'avg', 'min', 'max']
  const b = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : null
  if (!b || (b.kind != null && b.kind !== 'records')) return null
  const str = (v, max) => (typeof v === 'string' && v.trim() ? v.trim().slice(0, max) : null)
  const app = str(b.app, 80)
  const collection = str(b.collection, 80)
  if (!app || !collection) return null
  const filters = (Array.isArray(b.filters) ? b.filters : [])
    .filter((f) => f && typeof f === 'object' && str(f.field, 80) && OPS.includes(f.op))
    .slice(0, 8)
    .map((f) => ({
      field: str(f.field, 80),
      op: f.op,
      value: f.value === undefined ? null : f.value
    }))
  const p = b.period && typeof b.period === 'object' ? b.period : null
  // ONE clock per answer: an answer's own block carries the clock and the
  // absolute range the server computed its words on (server pinBlockPeriod:
  // asOf + timeZone | tzOffsetMinutes, with from / to). Kept whole, so the
  // records call re-reads THAT span — never "this quarter" again on the
  // browser's clock. Only form is checked here; the server validates.
  const iso = (v) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v) ? v.slice(0, 40) : null)
  const zone =
    p && typeof p.timeZone === 'string' && p.timeZone.trim()
      ? { timeZone: p.timeZone.trim().slice(0, 64) }
      : p && typeof p.tzOffsetMinutes === 'number' && Number.isFinite(p.tzOffsetMinutes)
        ? { tzOffsetMinutes: p.tzOffsetMinutes }
        : null
  const pin =
    p && iso(p.asOf) && zone
      ? {
          asOf: iso(p.asOf),
          ...zone,
          ...(iso(p.from) && iso(p.to) ? { from: iso(p.from), to: iso(p.to) } : {})
        }
      : null
  const period =
    p && str(p.field, 80) && PRESETS.includes(p.preset)
      ? { field: str(p.field, 80), preset: p.preset, ...(pin || {}) }
      : null
  const a = b.aggregate && typeof b.aggregate === 'object' ? b.aggregate : null
  const aggField = a ? str(a.field, 80) : null
  const aggregate =
    a && AGG.includes(a.op) && (a.op === 'count' || aggField)
      ? { op: a.op, ...(aggField ? { field: aggField } : {}) }
      : null
  const groupBy = str(b.groupBy, 80)
  const so = b.sort && typeof b.sort === 'object' ? b.sort : null
  const sort =
    so && str(so.field, 80)
      ? { field: str(so.field, 80), dir: so.dir === 'asc' ? 'asc' : 'desc' }
      : null
  const lim = Math.floor(Number(b.limit))
  const limit = Number.isFinite(lim) && b.limit != null ? Math.min(50, Math.max(1, lim)) : null
  const fields = (Array.isArray(b.fields) ? b.fields : [])
    .map((f) => str(f, 80))
    .filter(Boolean)
    .slice(0, 8)
  const view = VIEWS.includes(b.view) ? b.view : groupBy ? 'chart' : aggregate ? 'stat' : 'list'
  // The DISPLAY hint of a chart (contract ask A, optional): bars | line |
  // donut | chips. Display-only — the view model still checks the data can
  // take it (chips: the categories as pills, each its label and its count).
  const chart = ['bars', 'line', 'donut', 'chips'].includes(b.chart) ? b.chart : null
  const t = b.title
  const title =
    t && typeof t === 'object'
      ? {
          ...(str(t.key, 120) ? { key: str(t.key, 120) } : {}),
          ...(str(t.text, 120) ? { text: str(t.text, 120) } : {})
        }
      : str(t, 120)
        ? { text: str(t, 120) }
        : {}
  return {
    kind: 'records',
    app,
    collection,
    view,
    title,
    filters,
    ...(period ? { period } : {}),
    ...(aggregate ? { aggregate } : {}),
    ...(groupBy ? { groupBy } : {}),
    ...(sort ? { sort } : {}),
    ...(limit ? { limit } : {}),
    ...(fields.length ? { fields } : {}),
    ...(chart ? { chart } : {})
  }
}

// A raw key made readable: "job_openings" / "jobOpenings" → "Job openings".
// Registered on its own (FA204): recordsBlockTitle, the view's column labels
// and the empty note's field name all read it.
export const recordsHumanizeKey = function recordsHumanizeKey(key) {
  const words = String(key || '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : ''
}

// The block's words: its title key (resolved), its own text, else the
// collection's key made readable ("job_openings" → "Job openings"). The app's
// own prefix goes: a CRM block over `crm_deals` reads "Deals", never
// "Crm deals" (the source line already names the app).
export const recordsBlockTitle = function recordsBlockTitle(block) {
  const b = block && typeof block === 'object' ? block : {}
  const t = b.title && typeof b.title === 'object' ? b.title : {}
  if (t.key) {
    const said = this.call('polyglot', t.key)
    if (said && said !== t.key) return String(said)
  }
  if (t.text) return String(t.text)
  const app = String(b.app || '')
    .split('/')
    .pop()
    .replace(/-app$/, '')
    .toLowerCase()
  let key = String(b.collection || '')
  const lower = key.toLowerCase()
  for (const sep of ['.', '_', '-']) {
    if (app && lower.startsWith(app + sep) && key.length > app.length + 1) {
      key = key.slice(app.length + 1)
      break
    }
  }
  return this.call('recordsHumanizeKey', key)
}

// The period's words, from a literal map (a key is never built by
// concatenation — i18nWorkspaceExtraction).
export const recordsPeriodLabel = function recordsPeriodLabel(block) {
  const KEYS = {
    today: 'workspace.assistantRecords.periodToday',
    thisWeek: 'workspace.assistantRecords.periodThisWeek',
    thisMonth: 'workspace.assistantRecords.periodThisMonth',
    thisQuarter: 'workspace.assistantRecords.periodThisQuarter',
    lastQuarter: 'workspace.assistantRecords.periodLastQuarter',
    thisYear: 'workspace.assistantRecords.periodThisYear'
  }
  const p = block && block.period && typeof block.period === 'object' ? block.period : null
  // A PINNED answer (the server's pin: asOf + a zone + from / to) names its
  // FIXED range — "1 Oct – 31 Dec 2026" in the pin's own time zone — never a
  // relative word: an old pinned answer must not claim "this quarter" over
  // another quarter's range. `to` is exclusive (the range end), so the last
  // day shown is the day before it.
  const ms = (v) => (typeof v === 'string' ? Date.parse(v) : NaN)
  const from = p && p.asOf ? ms(p.from) : NaN
  const to = p && p.asOf ? ms(p.to) : NaN
  const zoned =
    p && typeof p.timeZone === 'string' && p.timeZone
      ? { timeZone: p.timeZone }
      : p && typeof p.tzOffsetMinutes === 'number' && Number.isFinite(p.tzOffsetMinutes)
        ? { shift: p.tzOffsetMinutes }
        : null
  if (zoned && Number.isFinite(from) && Number.isFinite(to) && to > from) {
    // a fixed offset: shift the instant and read it in UTC
    const at = (t) => (zoned.shift != null ? new Date(t + zoned.shift * 60000) : new Date(t))
    const tz = zoned.shift != null ? 'UTC' : zoned.timeZone
    const year = (t) =>
      Number(
        this.call('localizedDate', at(t), { year: 'numeric', timeZone: tz }).replace(/\D/g, '')
      )
    const day = (t, withYear) =>
      String(
        this.call('localizedDate', at(t), {
          day: 'numeric',
          month: 'short',
          ...(withYear ? { year: 'numeric' } : {}),
          timeZone: tz
        })
      )
    const last = to - 1
    const a = day(from, false)
    const b = day(last, true)
    try {
      if (day(from, true) === b) return b
      if (year(from) !== year(last)) return day(from, true) + ' – ' + b
    } catch (_) {
      return a + ' – ' + b
    }
    return a + ' – ' + b
  }
  const preset = p && p.preset
  return KEYS[preset] ? String(this.call('polyglot', KEYS[preset])) : ''
}

// The cache entry of one block for the workspace on screen:
// { status: 'loading'|'ready'|'empty'|'unavailable'|'denied'|'failed', answer? }
// — or null before the first read. Every key is read up front (reactive
// callers subscribe to all of them).
export const assistantRecordsEntry = function assistantRecordsEntry(root, block) {
  const r = root && typeof root === 'object' ? root : {}
  const bag = r._aiRecords && typeof r._aiRecords === 'object' ? r._aiRecords : {}
  const ws = r.workspace
  const wsId = ws && (ws.id || ws._id) ? String(ws.id || ws._id) : ''
  if (!block) return null
  const key = wsId + ':' + this.call('boardSpecHash', block)
  return bag[key] || null
}

// Read the block once per workspace: mark it loading (the skeleton shows at
// once), ask the SDK, write the answer. A block already read, loading or
// refused is not asked again; `force` (Try again) re-asks a failed one and
// `'refresh'` (the board's Refresh) any answer not in flight. The
// bag keeps the 40 newest answers. An answer lands while the member is still
// in the SAME workspace — compared by id: the shell re-publishes `workspace`
// as a new object during boot, and an identity check dropped every answer
// that crossed it, leaving the cards on their skeleton for good (qa round 11,
// a cold `?board=` load).
export const assistantRecordsEnsure = function assistantRecordsEnsure(el, s, force) {
  const host = el || this
  const root = (s && s.root) || (host && host.state && host.state.root)
  const block = host.call('recordsBlockOf', s)
  if (!root || typeof root.update !== 'function' || !block) return null
  const ws = root.workspace
  const wsId = ws && (ws.id || ws._id) ? String(ws.id || ws._id) : ''
  const key = wsId + ':' + host.call('boardSpecHash', block)
  const bag = root._aiRecords && typeof root._aiRecords === 'object' ? root._aiRecords : {}
  const have = bag[key]
  // The answer's words come back in the session language (RECORDS RESULT
  // v1.1 `lang`), so an answer read in another language is asked ONCE again
  // after a language switch — stamped with the language it was asked in, so a
  // server that answers in another language never loops.
  let lang = ''
  try {
    lang = String(host.call('getActiveLang') || '')
  } catch (_) {
    lang = ''
  }
  const otherLang = !!(have && have.lang && lang && have.lang !== lang && have.status !== 'loading')
  // `force` true = Try again (a failed read only); 'refresh' = the board's
  // Refresh (D3): any settled answer is asked again, never one in flight.
  const again =
    (force === true && have && have.status === 'failed') ||
    (force === 'refresh' && have && have.status !== 'loading')
  if (have && !otherLang && !again) return have
  const write = (entry) => {
    const now = root._aiRecords && typeof root._aiRecords === 'object' ? root._aiRecords : {}
    const keys = Object.keys(now).filter((k) => k !== key)
    const kept = {}
    for (const k of keys.slice(-39)) kept[k] = now[k]
    kept[key] = entry
    root.update({ _aiRecords: kept }, { preventFetch: true })
  }
  write({ status: 'loading', at: Date.now(), ...(lang ? { lang } : {}) })
  const land = (entry) => {
    const cur = root._aiRecords && root._aiRecords[key]
    const now = root.workspace
    const nowId = now && (now.id || now._id) ? String(now.id || now._id) : ''
    if (nowId !== wsId || !cur || cur.status !== 'loading') return
    write({ ...entry, at: Date.now(), ...(lang ? { lang } : {}) })
  }
  Promise.resolve()
    .then(() => host.call('assistantRecordsFetch', block, wsId || null, lang || null))
    .then(
      (entry) => land(entry && entry.status ? entry : { status: 'failed' }),
      () => land({ status: 'failed' })
    )
  return root._aiRecords && root._aiRecords[key]
}

// ONE records answer (the `ai.boards.records` shape — from the SDK, or from a
// fly page's stream, functions/assistantFly.js) → the cache entry the
// renderer reads: { status: 'ready' | 'empty', answer }.
export const assistantRecordsAnswer = function assistantRecordsAnswer(res, block) {
  const r = res && typeof res === 'object' ? res : {}
  const rows = Array.isArray(r.rows) ? r.rows.slice(0, 50) : []
  const groups = Array.isArray(r.groups) ? r.groups.slice(0, 24) : []
  const total = Number(r.total) || 0
  const aggregate = r.aggregate && typeof r.aggregate === 'object' ? r.aggregate : null
  // RECORDS RESULT v1.1 — every field optional (an older server sends none):
  // the view the SERVER drew (a list or table grouped by a field comes back
  // as a chart: `adjusted` { path: 'view', reason: 'grouped' }), the unit of
  // the numbers (money + its currency, a count, a plain number), what the
  // groups are (groupField, and groupUnit for a time series), the language
  // of the words, and a stat's series for its sparkline.
  const VIEWS = ['stat', 'list', 'table', 'chart']
  const UNITS = ['day', 'week', 'month']
  const adjusted = Array.isArray(r.adjusted) ? r.adjusted : []
  const grouped = adjusted.some((a) => a && a.path === 'view' && a.reason === 'grouped')
  const served =
    r.block && typeof r.block === 'object' && VIEWS.includes(r.block.view)
      ? r.block.view
      : grouped
        ? 'chart'
        : null
  const u = r.unit && typeof r.unit === 'object' ? r.unit : null
  const unit =
    u && ['count', 'money', 'number'].includes(u.kind)
      ? u.kind === 'money'
        ? {
            kind: 'money',
            currency: typeof u.currency === 'string' && u.currency ? u.currency : null
          }
        : { kind: u.kind }
      : null
  const gf = r.groupField && typeof r.groupField === 'object' ? r.groupField : null
  const answer = {
    rows,
    groups,
    total,
    aggregate,
    fields: Array.isArray(r.fields) ? r.fields : [],
    period: r.period || null,
    truncated: !!r.truncated,
    view: served,
    unit,
    groupField:
      gf && typeof gf.key === 'string'
        ? {
            key: gf.key,
            label: typeof gf.label === 'string' ? gf.label : '',
            type: typeof gf.type === 'string' ? gf.type : null
          }
        : null,
    groupUnit: UNITS.includes(r.groupUnit) ? r.groupUnit : null,
    lang: typeof r.lang === 'string' && r.lang ? r.lang : null,
    series: Array.isArray(r.series)
      ? r.series.filter((p) => p && typeof p === 'object').slice(0, 90)
      : null,
    seriesUnit: UNITS.includes(r.seriesUnit) ? r.seriesUnit : null,
    warnings: Array.isArray(r.warnings)
      ? r.warnings.filter((w) => typeof w === 'string').slice(0, 8)
      : [],
    // WHY a block matches nothing (total 0 under its filters / period): the
    // rows each ONE condition would let through if it were left out, largest
    // first — `rows` 0 means the collection itself is empty.
    empty:
      r.empty && typeof r.empty === 'object'
        ? {
            rows: Number(r.empty.rows) || 0,
            without: (Array.isArray(r.empty.without) ? r.empty.without : [])
              .filter((w) => w && typeof w === 'object' && Number(w.count) > 0)
              .slice(0, 4)
              .map((w) => ({
                path: typeof w.path === 'string' ? w.path : '',
                field: typeof w.field === 'string' ? w.field : '',
                count: Number(w.count)
              }))
          }
        : null,
    // A near-miss value the server read as the real one (`adjusted`
    // { path: 'filters[i].value', reason: 'snapped', from, to }).
    snapped: adjusted
      .filter(
        (a) =>
          a &&
          a.reason === 'snapped' &&
          ['string', 'number'].includes(typeof a.from) &&
          ['string', 'number'].includes(typeof a.to)
      )
      .slice(0, 3)
      .map((a) => ({ from: String(a.from).slice(0, 60), to: String(a.to).slice(0, 60) }))
  }
  const view = served || (block && block.view)
  const empty = view === 'stat' ? total === 0 : !rows.length && !groups.length
  return { status: empty ? 'empty' : 'ready', answer }
}

// ONE SDK call. The answer keeps only what the renderer reads; a stat whose
// set is empty, and a list / table / chart with no row and no group, are
// 'empty' (a sentence, never a bare zero). The op missing from the SDK or the
// server → 'unavailable'; a refused read → 'denied'; anything else 'failed'.
export const assistantRecordsFetch = async function assistantRecordsFetch(
  block,
  workspaceId,
  lang
) {
  let fixture = false
  try {
    fixture =
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('symbols_ai_records_fixture') === '1'
  } catch (_) {
    fixture = false
  }
  if (fixture) return this.call('assistantRecordsFixture', block)
  const sdk =
    this.sdk ||
    (this.context && this.context.sdk) ||
    (typeof this.call === 'function' ? this.call('getSDK') : null)
  if (!sdk || typeof sdk.execute !== 'function') return { status: 'unavailable' }
  try {
    // `lang` rides the call (the SDK after 7b767bc sends it; an older SDK
    // drops it and the server reads the member's own language).
    const res = await sdk.execute('ai.boards', 'records', {
      ...(workspaceId ? { workspaceId } : {}),
      block,
      ...(typeof lang === 'string' && lang ? { lang } : {})
    })
    return this.call('assistantRecordsAnswer', res, block)
  } catch (err) {
    const msg = String((err && err.message) || err || '')
    const status = Number(err && err.status)
    if (
      /unknown entity|does not support|unknown method|unknown op|not found on service|is not available/i.test(
        msg
      )
    ) {
      return { status: 'unavailable' }
    }
    if (status === 403 || /forbidden|denied|not allowed/i.test(msg)) return { status: 'denied' }
    return { status: 'failed' }
  }
}

// THE DEV FIXTURE — only behind localStorage `symbols_ai_records_fixture`
// ('1'), never in a product path: a deterministic answer shaped like the
// server's, so the four views render before `ai.boards.records` ships.
export const assistantRecordsFixture = function assistantRecordsFixture(block) {
  const b = block || {}
  const fields = (b.fields && b.fields.length ? b.fields : ['status', 'date']).slice(0, 3)
  const rows = []
  const n = Math.min(Number(b.limit) || 5, 8)
  for (let i = 0; i < n; i++) {
    const values = {}
    fields.forEach((f, j) => {
      values[f] =
        j === 0 ? ['open', 'won', 'lost'][i % 3] : '2026-09-' + String(10 + i).padStart(2, '0')
    })
    rows.push({ id: 'fx' + i, title: 'Fixture ' + (i + 1), values })
  }
  const groups = b.groupBy
    ? ['open', 'won', 'lost'].map((k, i) => ({ key: k, label: k, count: 8 - i * 3 }))
    : []
  const agg = b.aggregate || { op: 'count' }
  return {
    status: 'ready',
    answer: {
      rows,
      groups,
      total: 12,
      aggregate: {
        op: agg.op,
        ...(agg.field ? { field: agg.field } : {}),
        value: agg.op === 'count' ? 12 : 4800
      },
      fields: fields.map((k) => ({ key: k, type: k === 'date' ? 'date' : 'string' })),
      period: b.period || null,
      truncated: false,
      fixture: true
    }
  }
}

// The view a block RENDERS as: the server's own view when the answer names
// one (a list or table grouped by a field comes back as a chart), else the
// block's.
export const assistantRecordsViewOf = function assistantRecordsViewOf(block, entry) {
  const VIEWS = ['stat', 'list', 'table', 'chart']
  const served = entry && entry.answer ? entry.answer.view : null
  if (VIEWS.includes(served)) return served
  const own = block && block.view
  return VIEWS.includes(own) ? own : 'list'
}

// THE ONE GATE of a view: the answer is ready and draws as `view` — with no
// `view`, only "the answer is ready" (the holder of the view).
export const assistantRecordsShows = function assistantRecordsShows(s, view) {
  const block = this.call('recordsBlockOf', s)
  const entry = this.call('assistantRecordsEntry', s && s.root, block)
  if (!block || !entry || entry.status !== 'ready') return false
  return view ? this.call('assistantRecordsViewOf', block, entry) === view : true
}

// The view model of the block a surface carries — `(el, s) =>
// el.call('assistantRecordsViewFor', s).hero` from any part of the card.
export const assistantRecordsViewFor = function assistantRecordsViewFor(s) {
  const block = this.call('recordsBlockOf', s)
  if (!block) return {}
  return this.call(
    'assistantRecordsView',
    block,
    this.call('assistantRecordsEntry', s && s.root, block)
  )
}

// A date of an answer in the member's words (shared localizedDate: the session
// language, ICU's month names — Georgian included where the runtime has no
// Georgian data): a day or a week reads '29 Sep', a month 'Sep 2026'; a day
// outside this year carries its year. A date-only value ('2026-09-29') is a
// calendar day and is read in UTC, so no timezone moves it to the day before.
export const assistantRecordsDate = function assistantRecordsDate(value, unit) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value == null ? '' : String(value)
  }
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value)
  const d = new Date(dateOnly ? value + 'T00:00:00Z' : value)
  if (Number.isNaN(d.getTime())) return value.slice(0, 10)
  const utc = dateOnly ? { timeZone: 'UTC' } : {}
  const year = dateOnly ? d.getUTCFullYear() : d.getFullYear()
  const opts =
    unit === 'month'
      ? { month: 'short', year: 'numeric', ...utc }
      : {
          day: 'numeric',
          month: 'short',
          ...(year !== new Date().getFullYear() ? { year: 'numeric' } : {}),
          ...utc
        }
  const out = this.call('localizedDate', d, opts)
  return out ? String(out) : value.slice(0, 10)
}

// The view model the renderer paints, from the block and its cache entry:
//   stat   { value, currency list, hero { number, unit }, delta, spark }
//   list   rows [{ key, title, meta, row }] — plus { appRow, record, fields }
//          when the app draws its own row (assistantRecordsRowOf below)
//   table  columns [{ key, label }] + rows [{ key, cells: [{ key, text }], row }]
//   chart  BY THE DATA'S SHAPE (render step 2): groups over TIME (groupUnit,
//          or a date groupField) draw a line — chart 'line', line { points,
//          first, last, value, number, unit }; categories draw bars — chart
//          'bars', bars [{ key, label, value, share (0..1) }], largest first,
//          at most 8: the rest folds into "Other" (the server's own word for
//          its fold) when the numbers add up (a count, a sum), and an average
//          never folds. The display hint 'chips' on categories gives chart
//          'chips', chips [{ key, label, value, drillable }] (the bars' order
//          and fold, no scale) and bars [].
// Every word is the member's (RECORDS RESULT v1.1): a column's label, an enum
// value's label, a group's label; a raw key shows only when the server sent no
// words for it. Money is the ISO code + the compact amount ("GEL 12.5K") and
// only when the answer names the currency — a row or a unit without one shows
// the bare number, never a default currency. Dates read as dates ("29 Sep").
export const assistantRecordsView = function assistantRecordsView(block, entry) {
  const b = block || {}
  const ans = entry && entry.answer ? entry.answer : {}
  const fieldMeta = Array.isArray(ans.fields) ? ans.fields : []
  const metaOf = (key) => fieldMeta.find((x) => x && x.key === key) || null
  const labelOf = (key) => {
    const f = metaOf(key)
    if (f && typeof f.label === 'string' && f.label.trim()) return f.label
    return this.call('recordsHumanizeKey', key)
  }
  // One value of a row, in words: the enum value's label, money with the row's
  // own currency, a compact number, a date, a mark for a yes/no.
  const valueText = (key, v, values) => {
    if (v === null || v === undefined || v === '') return '—'
    const f = metaOf(key)
    if (f && Array.isArray(f.values)) {
      const hit = f.values.find((x) => x && String(x.key) === String(v))
      // A label that only repeats the code ("in_review" for in_review) is no
      // label: the rule below gives the words.
      if (hit && typeof hit.label === 'string' && hit.label && hit.label !== String(v)) {
        return hit.label
      }
    }
    // A category value with no label of its own (`in_review`, `needs-response`)
    // reads as its words — never the code (QA on dev, VaultOS: "in_review"
    // where the app says "Awaiting review"). Only an enum or a status-like
    // field: free text and ids keep their spelling.
    if (
      typeof v === 'string' &&
      /^[a-z0-9]+(?:[_-][a-z0-9]+)+$/.test(v) &&
      ((f && (Array.isArray(f.values) || f.type === 'enum')) ||
        /^(status|state|stage|kind|type|severity|priority|phase)$/i.test(String(key)))
    ) {
      const words = v.replace(/[_-]+/g, ' ')
      return words.charAt(0).toUpperCase() + words.slice(1)
    }
    if (typeof v === 'number') {
      if (f && f.unit === 'money') {
        const cur = values && typeof values.currency === 'string' ? values.currency : ''
        return this.call('homeMoney', v, cur)
      }
      return this.call('homeCompactNumber', v)
    }
    // A yes/no in WORDS, both ways. It was `✓` / `—`: a `false` read as an
    // empty cell (QA on dev, VaultOS rules: "Enabled" showed "—" on all 11
    // rows). The field's own label for the value wins above (`values`).
    if (typeof v === 'boolean') {
      return this.call(
        'polyglot',
        v ? 'workspace.assistantRecords.yes' : 'workspace.assistantRecords.no'
      )
    }
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v) && (!f || f.type === 'date')) {
      return this.call('assistantRecordsDate', v, 'day')
    }
    return String(v).slice(0, 80)
  }
  // The unit of the answer's numbers (aggregate, groups): money with its
  // currency, else a count or a plain number. An older answer without `unit`
  // keeps its aggregate currency.
  const unit = ans.unit && typeof ans.unit === 'object' ? ans.unit : null
  const agg = ans.aggregate && typeof ans.aggregate === 'object' ? ans.aggregate : {}
  const money = unit ? unit.kind === 'money' : !!agg.currency
  const currency = money ? (unit && unit.currency) || agg.currency || '' : ''
  const numberText = (n) =>
    money ? this.call('homeMoney', n, currency) : this.call('homeCompactNumber', n)
  // THE HERO pair (the home widget's): the numeral and its unit word apart —
  // the money code leads ("GEL" · "64.6K"); a count or a number has no word.
  const code = String(currency || '').trim()
  const heroOf = (n, asMoney) => ({
    number: asMoney ? this.call('homeCompactNumber', n, 1000) : this.call('homeCompactNumber', n),
    unit: asMoney && code ? (/^[a-z]{3}$/i.test(code) ? code.toUpperCase() : code) : ''
  })
  const keys = fieldMeta.length
    ? fieldMeta.map((f) => f.key)
    : Array.isArray(b.fields)
      ? b.fields
      : []
  const rows = Array.isArray(ans.rows) ? ans.rows : []
  const out = {
    view: this.call('assistantRecordsViewOf', b, entry),
    total: Number(ans.total) || 0,
    truncated: !!ans.truncated
  }
  // A list or table that holds fewer rows than match says so ("20 of 46
  // shown") — Fly QA on dev, Unreal deals: 20 rows of 46 with no marker.
  const moreOf = (n) =>
    out.total > n
      ? String(this.call('polyglot', 'workspace.assistantRecords.shown'))
          .replace('{n}', this.call('homeCompactNumber', n))
          .replace('{total}', this.call('homeCompactNumber', out.total))
      : ''
  if (out.view === 'stat') {
    const byCur = Array.isArray(agg.byCurrency) ? agg.byCurrency : []
    out.value = agg.value == null ? (byCur.length ? '' : '—') : numberText(agg.value)
    out.currencies = byCur.map((c) => ({
      key: String(c.currency),
      text: this.call('homeMoney', c.value, c.currency)
    }))
    const v = typeof agg.value === 'number' && Number.isFinite(agg.value) ? agg.value : null
    out.hero = v == null ? null : heroOf(v, money)
    // THE TREND: the same measure over the span before (aggregate.previous,
    // null when not comparable) — "↑ 12% vs previous period"; never a fake 0%.
    const prev =
      typeof agg.previous === 'number' && Number.isFinite(agg.previous) ? agg.previous : null
    // In an OPEN period with rows dated ahead the server also sends the value
    // TO DATE (aggregate.toDate): the trend compares that with the span before
    // (cut at the same point), like with like; the hero keeps the whole value.
    const soFar = typeof agg.toDate === 'number' && Number.isFinite(agg.toDate) ? agg.toDate : v
    out.delta = ''
    if (soFar != null && prev != null && prev !== 0) {
      const pct = ((soFar - prev) / Math.abs(prev)) * 100
      const r = Math.abs(pct) >= 10 ? Math.round(pct) : Math.round(pct * 10) / 10
      const arrow = r > 0 ? '↑' : r < 0 ? '↓' : '→'
      out.delta = String(
        this.call('polyglot', 'workspace.assistantRecords.deltaVsPrevious')
      ).replace('{delta}', arrow + ' ' + Math.abs(r) + '%')
    }
    // THE SPARK: the period bucketed by its date field (`series`) — two points
    // or more, else none.
    const pts = (Array.isArray(ans.series) ? ans.series : [])
      .map((p) => (p && typeof p === 'object' ? Number(p.value) : NaN))
      .filter((n) => Number.isFinite(n))
    out.spark = pts.length >= 2 ? pts : null
    return out
  }
  if (out.view === 'chart') {
    const groups = (Array.isArray(ans.groups) ? ans.groups : []).filter(
      (g) => g && typeof g === 'object'
    )
    const value = (g) => (typeof g.value === 'number' ? g.value : Number(g.count) || 0)
    const valueText = (g) =>
      typeof g.value === 'number' ? numberText(g.value) : this.call('homeCompactNumber', value(g))
    const gf = ans.groupField && typeof ans.groupField === 'object' ? ans.groupField : null
    const unitOf = ans.groupUnit || (gf && gf.type === 'date' ? 'day' : '')
    if (unitOf) {
      // TIME: a line, oldest to newest (the server's order); the newest value
      // is the one the eye wants — it rides the accent dot's end of the line.
      const last = groups[groups.length - 1]
      out.chart = 'line'
      out.bars = []
      out.line = groups.length
        ? {
            points: groups.map(value),
            first: this.call('assistantRecordsDate', String(groups[0].key), unitOf),
            last: this.call('assistantRecordsDate', String(last.key), unitOf),
            // the newest value: whole (the accessible name), and as the home
            // hero draws it — the numeral and its money code apart
            value: valueText(last),
            ...heroOf(value(last), money && typeof last.value === 'number')
          }
        : null
      return out
    }
    // CATEGORIES: bars, largest first; "Other" (the server's own fold) last.
    const other = groups.filter((g) => g.key === '__other')
    let list = groups.filter((g) => g.key !== '__other').sort((a, b) => value(b) - value(a))
    const op = (agg && agg.op) || (b.aggregate && b.aggregate.op) || 'count'
    const MAX = 8
    if (list.length + other.length > MAX) {
      if (op === 'count' || op === 'sum') {
        const rest = [...list.slice(MAX - 1), ...other]
        list = list.slice(0, MAX - 1)
        const folded = {
          key: '__other',
          label: String(this.call('polyglot', 'workspace.assistantRecords.other')),
          count: rest.reduce((n, g) => n + (Number(g.count) || 0), 0)
        }
        if (rest.some((g) => typeof g.value === 'number')) {
          folded.value = rest.reduce((n, g) => n + (typeof g.value === 'number' ? g.value : 0), 0)
        }
        list.push(folded)
      } else {
        list = list.slice(0, MAX)
      }
    } else {
      list = [...list, ...other]
    }
    // ONE row per category: the bar's, the donut legend's and the chip's
    // words, value and drill.
    const rowOf = (g, i) => {
      const key = String(g.key == null ? i : g.key)
      return {
        key,
        label: typeof g.label === 'string' && g.label ? g.label : key,
        value: valueText(g),
        // D2 DRILL-DOWN: a category opens its records (assistantRecordsDrillOf);
        // the fold ("Other") and the empty group name no one value.
        drillable: !!b.groupBy && key !== '__other' && key !== ''
      }
    }
    // CHIPS (the display hint 'chips', board binding A′): the categories as
    // one wrapped row of pills, each its label and its count, in the bars'
    // order and fold, with no scale. Categories only: a time grouping keeps
    // its line (above).
    if (b.chart === 'chips') {
      out.chart = 'chips'
      out.bars = []
      out.chips = list.map(rowOf)
      return out
    }
    const max = list.reduce((m, g) => Math.max(m, value(g)), 0) || 1
    // D5 THE DONUT — drawn by the data's SHAPE: parts of a whole (a count or
    // a sum, none negative) in 2 to 5 groups with "Other" are a donut, the
    // rest bars. The display hint (`chart`, contract ask A) wins when the
    // data can take it: 'bars' always, 'donut' for parts of a whole in 2 to
    // 8 groups. The legend rows are the bar rows (drillable the same way);
    // each slice one opacity step of the card hue — never a raw colour.
    const sum = list.reduce((n, g) => n + value(g), 0)
    const parts = (op === 'count' || op === 'sum') && sum > 0 && list.every((g) => value(g) >= 0)
    const hint = b.chart
    const donut =
      parts &&
      list.length >= 2 &&
      (hint === 'donut' ? list.length <= 8 : hint === 'bars' ? false : list.length <= 5)
    const STEPS = [1, 0.72, 0.5, 0.34, 0.22, 0.14, 0.1, 0.07]
    const round2 = (n) => Math.round(n * 100) / 100
    out.chart = donut ? 'donut' : 'bars'
    out.bars = list.map((g, i) => {
      const { key, label, value: text, drillable } = rowOf(g, i)
      const row = {
        key,
        label,
        value: text,
        share: donut
          ? Math.max(0, Math.min(1, value(g) / sum))
          : Math.max(0, Math.min(1, value(g) / max)),
        drillable
      }
      if (donut) {
        row.dot = STEPS[i] || STEPS[STEPS.length - 1]
        row.shareText = Math.round(row.share * 100) + '%'
      }
      return row
    })
    // A 100-unit circumference: each slice's dash is its share, its offset
    // starts where the last one ended (25 = the top of the circle).
    let done = 0
    out.donut = donut
      ? {
          total: numberText(sum),
          slices: out.bars.map((r) => {
            const pct = round2(r.share * 100)
            const slice = {
              key: r.key,
              dash: pct + ' ' + round2(100 - pct),
              offset: String(round2(25 - done)),
              opacity: r.dot
            }
            done += pct
            return slice
          })
        }
      : null
    return out
  }
  if (out.view === 'table') {
    // Five columns, seven on a full-width card. When the answer names more,
    // references (which project, which building) yield to the row's own facts
    // (rooms, area, price); the columns keep the answer's order — Fly QA on
    // dev, Unreal "available apartments": price and area fell off behind
    // projectId and buildingId.
    const cap = b.size === 'xl' ? 7 : 5
    const isRef = (k) => {
      const f = metaOf(k)
      return !!(f && f.type === 'ref')
    }
    const keep = new Set(
      keys.length > cap
        ? [...keys.filter((k) => !isRef(k)), ...keys.filter(isRef)].slice(0, cap)
        : keys
    )
    const cols = keys.filter((k) => keep.has(k))
    out.columns = cols.map((k) => ({ key: k, label: labelOf(k) }))
    out.rows = rows.map((r) => {
      const vals = r.values && typeof r.values === 'object' ? r.values : {}
      const cells = []
      for (const k of cols) cells.push({ key: k, text: valueText(k, vals[k], vals) })
      return {
        key: String(r.id),
        title: r.title == null ? '' : String(r.title),
        cells,
        app: b.app || null,
        collection: b.collection || null,
        row: { id: String(r.id), ...(typeof r.href === 'string' ? { href: r.href } : {}) }
      }
    })
    out.more = moreOf(out.rows.length)
    return out
  }
  // The app's OWN row for this collection (manifest `collections[].row`),
  // when its module context is live — else the generic title + facts.
  const appRow = this.call('assistantRecordsRowOf', b)
  const fieldList = appRow
    ? fieldMeta.length
      ? fieldMeta
          .filter((f) => f && typeof f.key === 'string')
          .map((f) => ({
            key: f.key,
            type: f.type || null,
            ...(f.label ? { label: String(f.label) } : {})
          }))
      : keys.map((k) => ({ key: String(k), type: null }))
    : null
  // D5 THE STATUS LIST (Pulse's incidents): a status-like enum field —
  // one whose values carry the server's `tone` (contract ask E), else one
  // named status / state / stage / severity / priority — gives each row a
  // semantic tone from the design system (successInk / warningInk /
  // dangerInk / accentInk; none for a word it does not know) and its words;
  // the status leaves the row's facts.
  const TONES = {
    success: 'successInk',
    warning: 'warningInk',
    danger: 'dangerInk',
    info: 'accentInk'
  }
  const WORDS = {
    successInk: [
      'done',
      'complete',
      'completed',
      'won',
      'closed_won',
      'resolved',
      'paid',
      'approved',
      'published',
      'delivered',
      'healthy',
      'ok',
      'success',
      'succeeded',
      'confirmed',
      'active',
      'live'
    ],
    dangerInk: [
      'lost',
      'closed_lost',
      'failed',
      'failure',
      'blocked',
      'overdue',
      'cancelled',
      'canceled',
      'rejected',
      'critical',
      'urgent',
      'error',
      'breached',
      'expired',
      'declined'
    ],
    warningInk: [
      'pending',
      'waiting',
      'review',
      'in_review',
      'on_hold',
      'hold',
      'draft',
      'high',
      'at_risk',
      'warning',
      'due',
      'stuck',
      'paused'
    ],
    accentInk: ['new', 'open', 'in_progress', 'progress', 'scheduled', 'planned', 'todo', 'to_do']
  }
  const enumFields = fieldMeta.filter((f) => f && f.type === 'enum' && typeof f.key === 'string')
  const toned = enumFields.find(
    (f) => Array.isArray(f.values) && f.values.some((v) => v && TONES[v.tone])
  )
  const named = enumFields.find((f) => /(^|_)(status|state|stage|severity|priority)$/i.test(f.key))
  const statusField = toned || named || null
  const statusKey = statusField ? statusField.key : null
  const toneOf = (v) => {
    if (v == null || v === '') return null
    const hit = Array.isArray(statusField.values)
      ? statusField.values.find((x) => x && String(x.key) === String(v))
      : null
    if (hit && TONES[hit.tone]) return TONES[hit.tone]
    const word = String(v)
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_')
    for (const tone of Object.keys(WORDS)) if (WORDS[tone].includes(word)) return tone
    return null
  }
  out.rows = rows.map((r) => {
    const values = r.values && typeof r.values === 'object' ? r.values : {}
    const metaKeys = keys
      .filter((k) => k !== statusKey && values[k] != null && values[k] !== '')
      .slice(0, 2)
    const statusValue = statusKey ? values[statusKey] : null
    const item = {
      tone: statusKey ? toneOf(statusValue) : null,
      status:
        statusKey && statusValue != null && statusValue !== ''
          ? valueText(statusKey, statusValue, values)
          : '',
      key: String(r.id),
      title:
        r.title == null || r.title === ''
          ? valueText(keys[0], values[keys[0]], values)
          : String(r.title),
      meta: metaKeys.map((k) => valueText(k, values[k], values)).join(' · '),
      app: b.app || null,
      collection: b.collection || null,
      row: { id: String(r.id), ...(typeof r.href === 'string' ? { href: r.href } : {}) }
    }
    if (!appRow) return item
    // THE ROW CONTRACT (workspaceProjects.js _normalizeRecordRows): the
    // records-plane shape the app already reads — `name` is the answer's
    // title, `data` its values (only the fields the answer asked for).
    item.appRow = appRow
    item.fields = fieldList
    item.record = {
      id: String(r.id),
      collection: appRow.key,
      name: r.title == null ? '' : String(r.title),
      data: { ...values }
    }
    return item
  })
  out.more = moreOf(out.rows.length)
  return out
}

// WHERE A BAR'S DRILL LANDS (D2) — so a bar is a control only where its click
// does something:
//   { from }        a card of the home's generated board that can take one
//                   (ready, not a preview — assistantBoardDrill);
//   { from, page }  a section of the person's OWN Fly page once that page is
//                   done (functions/assistantFly.js assistantFlyDrill; the
//                   cell says so: `drillsInto`). A call's page is the call's —
//                   its bars only read;
//   null            anywhere else (a thread card).
// `from` is the card's key.
export const assistantRecordsDrillFrom = function assistantRecordsDrillFrom(s) {
  if (!s || s.kind !== 'records' || typeof s.key !== 'string' || !s.key) return null
  if (typeof s.page === 'string' && s.page) {
    return s.drillsInto === s.page ? { from: s.key, page: s.page } : null
  }
  const root = s.root || (this && this.state && this.state.root) || null
  const b = root && root._aiBoard
  if (!b || typeof b !== 'object' || b.preview || b.status !== 'ready') return null
  const items = Array.isArray(b.items) ? b.items : []
  return items.some((it) => it && it.key === s.key) ? { from: s.key } : null
}

// The bar rows of a chart card — the bars, or the donut's legend (D5). Where a
// drill lands (assistantRecordsDrillFrom) each row carries it, so a row opens
// its records there; elsewhere the rows stay plain.
export const assistantRecordsBarRows = function assistantRecordsBarRows(s) {
  const list = this.call('assistantRecordsViewFor', s).bars || []
  const drill = list.length ? this.call('assistantRecordsDrillFrom', s) : null
  return drill ? list.map((b) => ({ ...b, ...drill })) : list
}

// D2 DRILL-DOWN (Pulse: a bars row opens the filtered canvas) — one CATEGORY
// of a grouped records block → the same collection as a LIST with one more
// filter (the group field eq the bar's key): the question's own filters,
// period, sort and fields are kept, the grouping and the aggregate go. The
// words it came from ride `drill` (assistantRecordsDrillWords). Null for the
// fold ("Other"), the empty group, a time series (a line has no bar to open)
// and a block that is not grouped. The server validates the new filter like
// any other (an unusable value drops the block, never widens it).
export const assistantRecordsDrillOf = function assistantRecordsDrillOf(block, entry, bar) {
  const b = this.call('recordsBlockNormalize', block)
  const ans = entry && entry.answer && typeof entry.answer === 'object' ? entry.answer : null
  const key = bar && bar.key != null ? String(bar.key) : ''
  if (!b || !b.groupBy || !ans || !key || key === '__other') return null
  const gf = ans.groupField && typeof ans.groupField === 'object' ? ans.groupField : null
  if (ans.groupUnit || (gf && gf.type === 'date')) return null
  const fieldLabel =
    gf && gf.key === b.groupBy && typeof gf.label === 'string' && gf.label.trim()
      ? gf.label.trim()
      : this.call('recordsHumanizeKey', b.groupBy)
  const valueLabel =
    bar && typeof bar.label === 'string' && bar.label.trim() ? bar.label.trim() : key
  const source = this.call('recordsBlockTitle', b)
  const filters = b.filters
    .filter((f) => f.field !== b.groupBy)
    .concat([{ field: b.groupBy, op: 'eq', value: key }])
    .slice(-8)
  return {
    block: {
      kind: 'records',
      app: b.app,
      collection: b.collection,
      view: 'list',
      title: { text: (source + ' · ' + valueLabel).slice(0, 120) },
      filters,
      ...(b.period ? { period: b.period } : {}),
      ...(b.sort ? { sort: b.sort } : {}),
      limit: 10,
      ...(b.fields ? { fields: b.fields } : {})
    },
    drill: { field: b.groupBy, fieldLabel, value: key, valueLabel, source }
  }
}

// The drill card's filter in words, in the records WHY line's voice:
// "Stage: Proposal · from Deals by stage". Empty without a drill.
export const assistantRecordsDrillWords = function assistantRecordsDrillWords(s) {
  const d = s && typeof s === 'object' && s.drill && typeof s.drill === 'object' ? s.drill : null
  if (!d || !d.fieldLabel || !d.valueLabel) return ''
  return String(this.call('polyglot', 'workspace.assistantRecords.drillFilter'))
    .replace('{field}', d.fieldLabel)
    .replace('{value}', d.valueLabel)
    .replace('{source}', d.source || '')
}

// A bar's click: the drill card joins the page the bar sits on, right after
// its card (`from`, the card's key) — a Fly page's (`page`) or the board's.
// Set only where a drill lands (assistantRecordsDrillFrom).
export const assistantRecordsDrill = function assistantRecordsDrill(bar) {
  if (!bar || !bar.drillable || !bar.from) return false
  const target = { key: bar.key, label: bar.label }
  if (typeof bar.page === 'string' && bar.page) {
    return this.call('assistantFlyDrill', bar.page, bar.from, target)
  }
  return this.call('assistantBoardDrill', bar.from, target)
}

// The app row for a block's collection — `{ key, row, moduleId }` from the
// record-row registry (functions/moduleRecordRows.js), or null. Null — the
// generic row — when no app declares one, when the app's module context is not
// live yet (a row mounted without it would miss the app's components and
// tokens; the list re-reads when the apps re-publish), or when this row failed
// to mount before (`root._aiRecordRowsBroken`). A block may name the
// collection by its catalogue id (`crm.crm_deals`): the stored key after the
// dot is tried too.
export const assistantRecordsRowOf = function assistantRecordsRowOf(block) {
  const b = block && typeof block === 'object' ? block : null
  const col = b && typeof b.collection === 'string' ? b.collection.trim().toLowerCase() : ''
  const root = this && this.state && this.state.root
  if (!col || !root || typeof root !== 'object') return null
  const getCtx = typeof root._moduleDsCtxGet === 'function' ? root._moduleDsCtxGet : null
  if (!getCtx) return null
  const dot = col.indexOf('.')
  const names = dot > 0 ? [col, col.slice(dot + 1)] : [col]
  let hit = null
  for (const name of names) {
    hit = this.call('moduleRecordRow', root, name)
    if (hit) break
  }
  if (!hit) return null
  const broken =
    root._aiRecordRowsBroken && typeof root._aiRecordRowsBroken === 'object'
      ? root._aiRecordRowsBroken
      : {}
  if (broken[hit.moduleId + '/' + hit.row]) return null
  if (!getCtx(hit.moduleId)) return null
  return { key: hit.key, row: hit.row, moduleId: hit.moduleId }
}

// Mount the app's row INTO the list row's host — the widget body's frame
// contract, for one record: `{ extends: row }` under the module's OWN context
// (its components, functions, tokens and state scope), with a COPY of its
// globalScope plus `scope.record` + `scope.fields` (the app's shared bag is
// never written). Idempotent per record + fields. A mount that throws marks
// the row broken for the session, and the list falls back to the generic row.
// el.call('assistantRecordsRowMount', hostEl, rowState)
export const assistantRecordsRowMount = function assistantRecordsRowMount(el, s) {
  const host = el || this
  const a = s && s.appRow
  if (!host || typeof host.set !== 'function' || !a || !a.row) return false
  const root = (s && s.root) || (host.state && host.state.root) || null
  const modCtx =
    root && typeof root._moduleDsCtxGet === 'function' ? root._moduleDsCtxGet(a.moduleId) : null
  if (!modCtx) return false
  const record = s.record && typeof s.record === 'object' ? s.record : null
  const fields = Array.isArray(s.fields) ? s.fields : []
  const sig =
    'row/' + (a.moduleId || '') + '/' + a.row + '/' + this.call('boardSpecHash', { record, fields })
  if (host.__recordsRowSig === sig) return true
  host.__recordsRowSig = sig
  const def = { extends: a.row, context: modCtx }
  def.scope = Object.assign({}, modCtx.globalScope || null, { record, fields })
  try {
    host.set(def)
    return true
  } catch (err) {
    console.warn(
      '[assistantRecords] app row mount failed, the generic row shows:',
      a.row,
      err && err.message
    )
    const bag =
      root._aiRecordRowsBroken && typeof root._aiRecordRowsBroken === 'object'
        ? root._aiRecordRowsBroken
        : {}
    if (typeof root.update === 'function') {
      root.update(
        { _aiRecordRowsBroken: { ...bag, [a.moduleId + '/' + a.row]: true } },
        { preventFetch: true }
      )
    }
    return false
  }
}

// Where a row (or the whole block) opens:
//   1. the row's own `href` when the server sends one;
//   2. ITS RECORD, on the page of the app that owns the collection — the
//      record-route registry (manifest `collections[].route` + `param`,
//      functions/moduleRecordRoutes.js): "/crm?deal=<id>"; the block itself
//      opens that page. A block may name the collection by its catalogue id
//      (`crm.crm_deals`): the stored key after the dot is tried too;
//   3. the app's own page: its navbar entry (`route`, or `path` — the
//      spelling a manifest's configDefaults.navbar uses), its launcher tile,
//      its first page.
// Through openInSlot — the PageSlot when the route has a slot page, else a
// navigation. Null when nothing names a page (the row then opens nothing, and
// the block offers no "Open in split").
export const assistantRecordsHref = function assistantRecordsHref(block, row) {
  if (row && typeof row.href === 'string' && row.href.startsWith('/')) return row.href
  const root = this.state && this.state.root
  const b = block && typeof block === 'object' ? block : {}
  const col = typeof b.collection === 'string' ? b.collection.trim().toLowerCase() : ''
  if (col && root) {
    const dot = col.indexOf('.')
    const names = dot > 0 ? [col, col.slice(dot + 1)] : [col]
    for (const name of names) {
      const where = this.call('moduleRecordRoute', root, name)
      if (!where || typeof where.route !== 'string' || !where.route.startsWith('/')) continue
      return row && row.id != null && row.id !== '' && where.param
        ? where.route + '?' + where.param + '=' + encodeURIComponent(String(row.id))
        : where.route
    }
  }
  const want = b.app ? String(b.app) : ''
  // The workspace's own project (the takeover module) first, then the
  // installed apps — the record-route registry's order.
  const apps = [
    ...(root && root.workspaceProject && typeof root.workspaceProject === 'object'
      ? [root.workspaceProject]
      : []),
    ...(root && Array.isArray(root.workspaceApps) ? root.workspaceApps : [])
  ]
  const app = apps.find((a) => {
    const id = a && a.id ? String(a.id) : ''
    const key = id.split('/').pop()
    return id === want || key === want || key.replace(/-app$/, '') === want
  })
  if (!app) return null
  const pathOf = (n) =>
    n && typeof n === 'object'
      ? [n.route, n.path].find((p) => typeof p === 'string' && p.startsWith('/')) || ''
      : ''
  const hit = [
    ...(Array.isArray(app.navbar) ? app.navbar : []),
    ...(Array.isArray(app.apps) ? app.apps : [])
  ]
    .map(pathOf)
    .find(Boolean)
  if (hit) return hit
  const pages = Array.isArray(app.pages) ? app.pages : []
  const first = pages.find((p) => typeof p === 'string' && p.startsWith('/'))
  return first || null
}

export const assistantRecordsOpen = function assistantRecordsOpen(block, row) {
  const href = this.call('assistantRecordsHref', block, row)
  if (!href) return false
  this.call('openInSlot', href, this)
  return true
}

// ONE quiet line under a block for what the reader must know about how the
// answer was read — a money sort that stays inside each currency
// ('sorted_per_currency': amounts in two currencies are never compared). A
// literal map, so every key is greppable; a warning without words says
// nothing ('mixed_currency' needs none — the stat splits per currency).
export const assistantRecordsWarning = function assistantRecordsWarning(s) {
  const KEYS = { sorted_per_currency: 'workspace.assistantRecords.sortedPerCurrency' }
  const block = this.call('recordsBlockOf', s)
  const entry = this.call('assistantRecordsEntry', s && s.root, block)
  if (!entry || !entry.answer || (entry.status !== 'ready' && entry.status !== 'empty')) return ''
  const out = []
  // a near-miss value the server read as the real one ("tower a" → "Tower A")
  for (const x of Array.isArray(entry.answer.snapped) ? entry.answer.snapped : []) {
    out.push(
      String(this.call('polyglot', 'workspace.assistantRecords.snapped'))
        .replace('{from}', x.from)
        .replace('{to}', x.to)
    )
  }
  const list = Array.isArray(entry.answer.warnings) ? entry.answer.warnings : []
  const hit = entry.status === 'ready' ? list.find((w) => KEYS[w]) : null
  if (hit) out.push(String(this.call('polyglot', KEYS[hit])))
  return out.join(' · ')
}

// The line under a stat: the period and how many records it counts.
export const assistantRecordsSub = function assistantRecordsSub(s) {
  const block = this.call('recordsBlockOf', s)
  const entry = this.call('assistantRecordsEntry', s && s.root, block)
  const total = entry && entry.answer ? Number(entry.answer.total) || 0 : 0
  const period = this.call('recordsPeriodLabel', block)
  const count = String(
    this.call(
      'polyglot',
      total === 1 ? 'workspace.assistantRecords.totalOne' : 'workspace.assistantRecords.total'
    )
  ).replace('{count}', this.call('homeCompactNumber', total))
  return period ? period + ' · ' + count : count
}

// The one sentence of every state that is not rows: nothing matches (with the
// block's own words), not available yet, not yours to read, failed. '' while
// loading or with rows.
export const assistantRecordsNote = function assistantRecordsNote(s) {
  const block = this.call('recordsBlockOf', s)
  const entry = this.call('assistantRecordsEntry', s && s.root, block)
  const status = entry && entry.status
  if (status === 'empty') {
    const title = this.call('recordsBlockTitle', block)
    const period = this.call('recordsPeriodLabel', block)
    const said = String(this.call('polyglot', 'workspace.assistantRecords.empty')).replace(
      '{title}',
      title
    )
    const base = period ? said + ' · ' + period : said
    // WHY (RECORDS RESULT v1.1 `empty`): the ONE condition that, left out,
    // lets the most records through — the period, or a filter by its field's
    // name. A collection with no rows at all has no reason to add.
    const why = entry.answer && entry.answer.empty ? entry.answer.empty : null
    const top = why && why.rows > 0 && why.without.length ? why.without[0] : null
    if (!top) return base
    const byPeriod = top.path === 'period'
    const meta = byPeriod
      ? null
      : (Array.isArray(entry.answer.fields) ? entry.answer.fields : []).find(
          (f) => f && f.key === top.field
        )
    const field = byPeriod
      ? ''
      : meta && typeof meta.label === 'string' && meta.label.trim()
        ? meta.label
        : this.call('recordsHumanizeKey', top.field)
    const reason = String(
      this.call(
        'polyglot',
        byPeriod
          ? 'workspace.assistantRecords.emptyOutsidePeriod'
          : 'workspace.assistantRecords.emptyWithoutFilter'
      )
    )
      .replace('{count}', this.call('homeCompactNumber', top.count))
      .replace('{field}', field)
    return base + ' · ' + reason
  }
  if (status === 'unavailable')
    return String(this.call('polyglot', 'workspace.assistantRecords.unavailable'))
  if (status === 'denied') return String(this.call('polyglot', 'workspace.assistantRecords.denied'))
  if (status === 'failed') return String(this.call('polyglot', 'workspace.assistantRecords.failed'))
  return ''
}

// THE CLIENT CAPABILITY — what THIS shell renders, for the server's capability
// gate (ai-boards recordsBlock.js pickClientCapabilities, lane current-23-aib):
// `{ records: 1 }` = it renders RECORDS BLOCK v1 (AssistantRecordsBlock, the
// one renderer boards and the thread share) AND resolves it (the SDK's
// `ai.boards.records`, sdk 3.14.796+). The board generate / refine send it as
// `capabilities` (functions/assistantBoard.js), a chat turn as
// `context.capabilities` (functions/simoneAiTransport.js). Without it the
// gated server offers no collections and emits no records item, so the shell
// never shows a block it cannot resolve. An SDK that predates the field
// ignores it: sdk 3.14.796 builds the board body from a closed key list, and
// every SDK forwards the chat context whole.
//
// The SDK the shell hands out (shared/functions/sdkClient.js getSDK) waits for
// its init: EVERY method answers a promise, `getService` too (live on dev
// fa5efb329 a synchronous read took the promise for "no records method" and
// the shell declared nothing). So this answers AT ONCE when it can — no
// renderer, no SDK, the answer already kept, an SDK that answers at once — and
// otherwise answers the PROMISE of the answer. Either way the answer is kept on
// the root (`_aiClientCaps`, `{ records: 1 | 0 }`, written only when it
// changes), so every later call answers at once. The board request awaits a
// promise; a chat turn uses only an answer in hand (the transport's install
// starts the read). `sdk` = the SDK the caller holds (else the element's, else
// getSDK).
//
// `openRecord: 1` rides beside it when this shell's select_page_item opens a
// workspace RECORD (functions/pageContext.js selectPageItem, its 'record'
// case): the server offers that type only to a shell that declares it (aib's
// gate). Known at once — the flag rides the registered selectPageItem itself
// (`opensRecords`), so a host whose selectPageItem has no record case never
// declares it, and it needs neither the records renderer nor the SDK read.
// `views: 1` / `forms: 1` ride the same way when the thread draws app views
// and app forms (the server's embed_view / propose_form gate), and `tour: 1`
// when the shell runs the quick tour (functions/aiStartTour.js — the server's
// start_tour gate).
// The answer: `{ records?: 1, openRecord?: 1, views?: 1, forms?: 1, tour?: 1 }`,
// or null when none; never
// throws, a rejection resolves the part known at once.
export const assistantClientCapabilities = function assistantClientCapabilities(sdk) {
  try {
    const ctx = (this && this.context) || {}
    const fns = ctx.functions || {}
    const opener = fns.selectPageItem
    const openRecord = typeof opener === 'function' && opener.opensRecords === 1
    const components = ctx.components || {}
    // APP VIEWS and APP FORMS (functions/assistantAppViews.js,
    // functions/assistantAppForms.js): known at once — the thread's block
    // component draws them and their functions are registered. The server
    // offers embed_view / propose_form only to a shell that declares them.
    const block = !!components.AssistantThreadBlock
    const views = block && typeof fns.assistantAppViewSources === 'function'
    const forms = block && typeof fns.assistantFormOpen === 'function'
    // THE QUICK TOUR: known at once — the assistant's entry and the tour
    // itself are registered. The server offers start_tour only then.
    const tour =
      typeof fns.aiStartTour === 'function' && typeof fns.onboardingTourStart === 'function'
    const answer = (records) => {
      const out = {}
      if (records) out.records = 1
      if (openRecord) out.openRecord = 1
      if (views) out.views = 1
      if (forms) out.forms = 1
      if (tour) out.tour = 1
      return Object.keys(out).length ? out : null
    }
    if (!components.AssistantRecordsBlock) return answer(0)
    const root = this && this.state && this.state.root
    const kept = root && root._aiClientCaps
    if (kept && typeof kept === 'object') return answer(Number(kept.records) >= 1 ? 1 : 0)
    const keep = (records) => {
      const now = root && root._aiClientCaps
      if (root && typeof root.update === 'function' && !(now && Number(now.records) === records)) {
        root.update({ _aiClientCaps: { records } }, { preventFetch: true })
      }
      return answer(records)
    }
    const client =
      sdk ||
      (this && this.sdk) ||
      ctx.sdk ||
      (this && typeof this.call === 'function' ? this.call('getSDK') : null)
    if (!client || client.isNullClient === true || typeof client.getService !== 'function') {
      return answer(0)
    }
    const has = (boards) => (boards && typeof boards.records === 'function' ? 1 : 0)
    let boards = null
    try {
      boards = client.getService('aiBoards')
    } catch (_) {
      return keep(0)
    }
    if (boards && typeof boards.then === 'function') {
      return Promise.resolve(boards).then(
        (svc) => keep(has(svc)),
        () => keep(0)
      )
    }
    return keep(has(boards))
  } catch (_) {
    return null
  }
}
